import { redirect } from 'react-router';
import ProjectHomeRoute from '@/routes/ProjectHomeRoute';
import { getProjects, removeMember } from '@/api/projects';

import { deletePathway, getPathway, getPathwayParams } from '@/api/pathways';
import { deleteSystem, getSystem } from '@/api/systems';
import EditPathwayModal from '@/components/pathways/EditPathwayModal';
import EditSystemModal from '@/components/systems/EditSystemModal';
import AddMemberModal from '@/components/members/AddMemberModal';
import featureConfig from '@/featureConfig';
import MoocHomeRoute from '@/components/mooc/MoocHomeRoute';
import { getPathwayTemplates } from '@/api/pathwayTemplates';
import { pathwayTemplatesActions } from '@/stores/pathwayTemplatesStore';

const dashboardRouter = {
  path: 'dashboard',
  element: featureConfig.enableMoocDashboard ? <MoocHomeRoute /> : <ProjectHomeRoute />,
  loader: async () => {
    if (featureConfig.enableMoocDashboard) {
      const pathwayTemplates = await getPathwayTemplates();
      pathwayTemplatesActions.setPathwayTemplates(pathwayTemplates.data);
    }
    return {};
  },
  handle: {
    crumb: () => 'Dashboard',
  },
  children: [
    {
      path: 'pathways/new',
      element: <EditPathwayModal title="New Pathway" />,
      action: EditPathwayModal.addAction,
    },
    {
      path: 'pathways/:pathwayId/edit',
      element: <EditPathwayModal title="Edit Pathway" />,
      loader: async ({ params: { pathwayId } }) => {
        const [
          { data: pathway },
          {
            data: { params },
          },
        ] = await Promise.all([getPathway(pathwayId), getPathwayParams(pathwayId)]);

        return { pathway, params };
      },
      action: EditPathwayModal.editAction,
    },
    {
      path: 'pathways/:pathwayId/destroy',
      action: async ({ params }) => {
        const { projectId, pathwayId } = params;
        try {
          await deletePathway(pathwayId);
          return redirect(`/projects/${projectId}/dashboard`);
        } catch {
          return redirect(`/projects/${projectId}/dashboard`);
        }
      },
    },
    {
      path: 'pathways/:pathwayId/duplicate',
      element: <EditPathwayModal title="Duplicate Pathway" />,
      loader: async ({ params: { pathwayId } }) => {
        const [
          { data: pathway },
          {
            data: { next: projectsNextPage, results: projects },
          },
        ] = await Promise.all([getPathway(pathwayId), getProjects()]);

        return { pathway, projects, projectsNextPage };
      },
      action: EditPathwayModal.duplicateAction,
    },
    {
      path: 'systems/new',
      element: <EditSystemModal action="add" title="New System" />,
      action: EditSystemModal.addAction,
    },
    {
      path: 'systems/:systemId/edit',
      element: <EditSystemModal action="edit" title="Rename System" />,
      loader: async ({ params: { systemId } }) => {
        const { data: system } = await getSystem(systemId);
        return { system };
      },
      action: EditSystemModal.editAction,
    },
    {
      path: 'systems/:systemId/destroy',
      action: async ({ params }) => {
        const { projectId, systemId } = params;
        await deleteSystem(systemId);
        return redirect(`/projects/${projectId}`);
      },
    },
    {
      path: 'systems/:systemId/duplicate',
      element: <EditSystemModal action="duplicate" title="Duplicate System" />,
      loader: async ({ params: { systemId } }) => {
        const [
          { data: system },
          {
            data: { next: projectsNextPage, results: projects },
          },
        ] = await Promise.all([getSystem(systemId), getProjects()]);

        return { projects, projectsNextPage, system };
      },
      action: EditSystemModal.duplicateAction,
    },
    {
      path: 'new-member',
      action: AddMemberModal.action,
      element: <AddMemberModal title="Add Member" />,
    },
    {
      path: 'edit-member',
      action: AddMemberModal.action,
      element: <AddMemberModal title="Edit Member" />,
    },
    {
      path: 'remove-member',
      action: async ({ request, params }) => {
        const form = await request.formData();
        const member = Object.fromEntries(form);
        const { projectId } = params;

        await removeMember(projectId, member);

        if (member.self) {
          return redirect('/');
        }

        return redirect(`/projects/${projectId}`);
      },
    },
  ],
};

export default dashboardRouter;
